(() => {
  const DRAG_THRESHOLD = 5;
  const FLICK_VELOCITY_THRESHOLD = 0.35;
  const MIN_FLICK_TRAVEL = 24;
  const AUTOPLAY_RESUME_DELAY = 140;
  const SLIDE_SELECTOR = '.slideshow__slide';

  class HpBannerCarouselO2 extends HTMLElement {
    constructor() {
      super();
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onClickCapture = this.onClickCapture.bind(this);
      this.onProgressPointerDown = this.onProgressPointerDown.bind(this);
      this.onProgressPointerMove = this.onProgressPointerMove.bind(this);
      this.onProgressPointerUp = this.onProgressPointerUp.bind(this);
      this.onTrackScroll = this.onTrackScroll.bind(this);
      this.updateProgressSize = this.updateProgressSize.bind(this);
      this.onDragStart = (event) => event.preventDefault();
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    }

    connectedCallback() {
      if (this.eventsConnected) return;

      this.track = this.querySelector('[data-hp-banner-track]');
      if (!this.track) return;
      this.progress = this.querySelector('[data-hp-banner-progress]');
      this.progressThumb = this.querySelector('[data-hp-banner-progress-thumb]');
      this.slideshow = this.querySelector('slideshow-component');
      this.slides = this.track.querySelectorAll(SLIDE_SELECTOR);

      this.track.addEventListener('pointerdown', this.onPointerDown);
      this.track.addEventListener('pointermove', this.onPointerMove);
      this.track.addEventListener('click', this.onClickCapture, true);
      this.track.addEventListener('dragstart', this.onDragStart);
      this.track.addEventListener('scroll', this.onTrackScroll);
      window.addEventListener('pointerup', this.onPointerUp);
      window.addEventListener('pointercancel', this.onPointerUp);
      window.addEventListener('resize', this.updateProgressSize);

      if (this.progressThumb) {
        this.progressThumb.addEventListener('pointerdown', this.onProgressPointerDown);
        this.progressThumb.addEventListener('pointermove', this.onProgressPointerMove);
        this.progressThumb.addEventListener('pointerup', this.onProgressPointerUp);
        this.progressThumb.addEventListener('pointercancel', this.onProgressPointerUp);
        this.updateProgressSize();
      }

      this.eventsConnected = true;
    }

    disconnectedCallback() {
      this.track?.removeEventListener('pointerdown', this.onPointerDown);
      this.track?.removeEventListener('pointermove', this.onPointerMove);
      this.track?.removeEventListener('click', this.onClickCapture, true);
      this.track?.removeEventListener('dragstart', this.onDragStart);
      this.track?.removeEventListener('scroll', this.onTrackScroll);
      window.removeEventListener('pointerup', this.onPointerUp);
      window.removeEventListener('pointercancel', this.onPointerUp);
      window.removeEventListener('resize', this.updateProgressSize);
      this.progressThumb?.removeEventListener('pointerdown', this.onProgressPointerDown);
      this.progressThumb?.removeEventListener('pointermove', this.onProgressPointerMove);
      this.progressThumb?.removeEventListener('pointerup', this.onProgressPointerUp);
      this.progressThumb?.removeEventListener('pointercancel', this.onProgressPointerUp);
      this.track?.classList.remove('is-dragging');
      this.progress?.classList.remove('is-dragging');
      clearTimeout(this.autoplayResumeTimeout);
      this.eventsConnected = false;
    }

    onPointerDown(event) {
      if (event.pointerType !== 'mouse') {
        this.activeScrollPointerId = event.pointerId;
        this.resumeAfterScroll = true;
        this.pauseAutoplay();
        clearTimeout(this.autoplayResumeTimeout);
        return;
      }
      if (event.button !== 0) return;

      this.dragStart = {
        pointerId: event.pointerId,
        clientX: event.clientX,
        scrollLeft: this.track.scrollLeft,
        lastX: event.clientX,
        lastMoveAt: event.timeStamp,
        velocity: 0,
        travel: 0,
      };
      this.dragged = false;
      this.resumeAfterScroll = true;
      this.pauseAutoplay();
      clearTimeout(this.autoplayResumeTimeout);
    }

    onPointerMove(event) {
      if (!this.dragStart || event.pointerId !== this.dragStart.pointerId) return;
      const isPrimaryButtonPressed = (event.buttons & 1) !== 0;
      if (!isPrimaryButtonPressed) {
        this.dragStart = null;
        this.dragged = false;
        this.track.classList.remove('is-dragging');
        this.scheduleAutoplayResume();
        return;
      }

      const distance = event.clientX - this.dragStart.clientX;
      if (!this.dragged && Math.abs(distance) > DRAG_THRESHOLD) {
        this.dragged = true;
        this.track.classList.add('is-dragging');
        this.track.setPointerCapture(event.pointerId);
        this.dragStart.lastX = event.clientX;
        this.dragStart.lastMoveAt = event.timeStamp;
      }
      if (!this.dragged) return;

      this.dragStart.travel = Math.max(this.dragStart.travel, Math.abs(distance));
      const elapsed = event.timeStamp - this.dragStart.lastMoveAt;
      if (elapsed > 0) {
        const sample = (event.clientX - this.dragStart.lastX) / elapsed;
        this.dragStart.velocity = this.dragStart.velocity * 0.6 + sample * 0.4;
        this.dragStart.lastX = event.clientX;
        this.dragStart.lastMoveAt = event.timeStamp;
      }

      this.track.scrollLeft = this.dragStart.scrollLeft - distance;
      event.preventDefault();
    }

    onPointerUp(event) {
      if (event.pointerId === this.activeScrollPointerId) {
        this.activeScrollPointerId = null;
        this.scheduleAutoplayResume();
        return;
      }
      if (!this.dragStart || event.pointerId !== this.dragStart.pointerId) return;

      if (this.track.hasPointerCapture(event.pointerId)) {
        this.track.releasePointerCapture(event.pointerId);
      }
      this.track.classList.remove('is-dragging');

      if (this.dragged) {
        this.suppressClick = true;
        const velocity =
          event.type === 'pointercancel' || this.dragStart.travel < MIN_FLICK_TRAVEL
            ? 0
            : Math.abs(this.dragStart.velocity);
        const direction = this.dragStart.velocity < 0 ? 1 : -1;
        this.snapToSlide(this.dragStart.scrollLeft, direction, velocity);
        window.setTimeout(() => {
          this.suppressClick = false;
        });
      }

      this.dragStart = null;
      this.scheduleAutoplayResume();
    }

    onProgressPointerDown(event) {
      if (event.pointerType === 'mouse' && event.button !== 0) return;

      this.progressDragStart = {
        pointerId: event.pointerId,
        clientX: event.clientX,
        scrollLeft: this.track.scrollLeft,
      };
      this.progressDragged = false;
      this.resumeAfterScroll = true;
      this.progressThumb.setPointerCapture(event.pointerId);
      this.pauseAutoplay();
      clearTimeout(this.autoplayResumeTimeout);
    }

    onProgressPointerMove(event) {
      if (!this.progressDragStart || event.pointerId !== this.progressDragStart.pointerId) return;

      const distance = event.clientX - this.progressDragStart.clientX;
      if (!this.progressDragged && Math.abs(distance) > DRAG_THRESHOLD) {
        this.progressDragged = true;
        this.progress.classList.add('is-dragging');
        this.track.classList.add('is-dragging');
      }
      if (!this.progressDragged) return;

      const { maxScroll, maxThumbTravel } = this.getProgressMetrics();
      if (maxThumbTravel > 0) {
        this.track.scrollLeft = Math.min(
          Math.max(this.progressDragStart.scrollLeft + distance * (maxScroll / maxThumbTravel), 0),
          maxScroll,
        );
        this.updateProgress();
      }

      event.preventDefault();
    }

    onProgressPointerUp(event) {
      if (!this.progressDragStart || event.pointerId !== this.progressDragStart.pointerId) return;

      if (this.progressThumb.hasPointerCapture(event.pointerId)) {
        this.progressThumb.releasePointerCapture(event.pointerId);
      }
      this.progress.classList.remove('is-dragging');
      this.track.classList.remove('is-dragging');

      if (this.progressDragged) {
        this.snapToSlide(this.progressDragStart.scrollLeft, 0, 0);
      }

      this.progressDragStart = null;
      this.scheduleAutoplayResume();
    }

    getProgressMetrics() {
      return {
        maxScroll: this.progressMaxScroll,
        maxThumbTravel: this.progressMaxThumbTravel,
      };
    }

    updateProgressSize() {
      if (!this.progress || !this.progressThumb) return;

      const firstControl = this.progress.querySelector('.hp-banner-carousel-o2__control, button');
      if (firstControl) this.progressThumb.style.width = `${firstControl.offsetWidth}px`;

      this.progressMaxScroll = Math.max(this.track.scrollWidth - this.track.clientWidth, 0);
      this.progressMaxThumbTravel = Math.max(this.progress.clientWidth - this.progressThumb.offsetWidth, 0);
      this.updateProgress();
    }

    updateProgress() {
      if (!this.progress || !this.progressThumb) return;

      const { maxScroll, maxThumbTravel } = this.getProgressMetrics();
      const progress = maxScroll > 0 ? this.track.scrollLeft / maxScroll : 0;
      this.progressThumb.style.transform = `translateX(${progress * maxThumbTravel}px)`;
    }

    onTrackScroll() {
      this.updateProgress();
      if (this.resumeAfterScroll && this.activeScrollPointerId === null && !this.dragStart && !this.progressDragStart) {
        this.scheduleAutoplayResume();
      }
    }

    snapToSlide(startScroll, direction, velocity) {
      const slideOffset =
        (this.slides[1]?.offsetLeft ?? 0) - (this.slides[0]?.offsetLeft ?? 0) || this.track.clientWidth;
      const startIndex = Math.round(startScroll / slideOffset);
      const closestIndex = Math.round(this.track.scrollLeft / slideOffset);
      const targetIndex = Math.min(
        Math.max(velocity >= FLICK_VELOCITY_THRESHOLD ? startIndex + direction : closestIndex, 0),
        this.slides.length - 1,
      );

      this.track.scrollTo({
        left: targetIndex * slideOffset,
        behavior: this.reducedMotion.matches ? 'auto' : 'smooth',
      });
    }

    resumeAutoplay() {
      if (
        this.slideshow?.autoplayButtonIsSetToPlay &&
        !this.slideshow.pausedByThemeEditor &&
        !this.slideshow.matches(':hover') &&
        !this.slideshow.contains(document.activeElement) &&
        !this.reducedMotion.matches
      ) {
        this.slideshow.play();
      }
    }

    pauseAutoplay() {
      this.slideshow?.pause?.();
    }

    scheduleAutoplayResume() {
      clearTimeout(this.autoplayResumeTimeout);
      this.autoplayResumeTimeout = window.setTimeout(() => {
        this.resumeAfterScroll = false;
        this.resumeAutoplay();
      }, AUTOPLAY_RESUME_DELAY);
    }

    onClickCapture(event) {
      if (!this.suppressClick) return;
      event.preventDefault();
      event.stopPropagation();
      this.suppressClick = false;
    }
  }

  if (!customElements.get('hp-banner-carousel-o2')) {
    customElements.define('hp-banner-carousel-o2', HpBannerCarouselO2);
  }
})();
